import { useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Eye,
  Image as ImageIcon, // El icono de imagen se llamará ImageIcon
  ChevronUp,          // La flecha se llamará ChevronUp
  StickyNote
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import BotonEliminar from "../BotonEliminar/BotonEliminar";
import BotonEditar from "../BotonEditar/BotonEditar";
import Select from "react-select";
import { urlOptimizada, precargarImagen } from "../../utils/imagenes";

export default function VerServicios() {
  const [servicios, setServicios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [loading, setLoading] = useState(false);

  const [imagenesModal, setImagenesModal] = useState([]);
  const [indiceImagen, setIndiceImagen] = useState(0);
  const [comentarioModal, setComentarioModal] = useState(null);
  const [modeloOrdeniadora, setModeloOrdeniadora] = useState(null);

  const [zoom, setZoom] = useState(1);
  const [rotacion, setRotacion] = useState(0);
  // URL de la foto del visor que ya terminó de cargar (para mostrar el "cargando...")
  const [imagenCargada, setImagenCargada] = useState("");

  // Densidad de la tabla de escritorio ("compacta" | "comoda"), recordada en cada PC
  const [densidad, setDensidad] = useState(() => {
    try {
      return localStorage.getItem("historial_densidad") === "comoda" ? "comoda" : "compacta";
    } catch {
      return "compacta";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("historial_densidad", densidad);
    } catch {
      // sin almacenamiento disponible: no pasa nada
    }
  }, [densidad]);
  const compacta = densidad === "compacta";


  useEffect(() => {
    obtenerServicios();
    obtenerClientes();
  }, []);

  const obtenerClientes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/cliente`);
      setClientes(res.data);
    } catch (err) {
      toast.error("❌ Error al cargar clientes");
    }
  };

  const obtenerServicios = async (clienteId) => {
    try {
      setLoading(true);
      let res;
      if (!clienteId || clienteId === "todos") {
        res = await axios.get(`${process.env.REACT_APP_BASE_URL}/servicio`);
      } else {
        res = await axios.get(`${process.env.REACT_APP_BASE_URL}/servicio/cliente/${clienteId}`);
      }

      // 🛠️ PARSEAR IMÁGENES ANTES DE GUARDAR EN EL ESTADO
      const serviciosParseados = res.data.map(s => {
        let imagenes = [];
        if (s.imagenes_remito) {
          try {
            // Intentamos parsear el string ["url1", "url2"]
            imagenes = typeof s.imagenes_remito === 'string'
              ? JSON.parse(s.imagenes_remito)
              : s.imagenes_remito;
          } catch (e) {
            // Si falla el parse (es una sola URL), lo metemos en un array
            imagenes = [s.imagenes_remito];
          }
        }
        return { ...s, imagenes_remito: imagenes };
      });

      // 📅 ORDENAR por la fecha próxima más lejana (próx. servicio técnico o próx. cambio de pezoneras)
      const fechaProximaMayor = (s) => {
        const fechas = [s.proximo_servicio, s.proximo_recambio_pezoneras]
          .filter(Boolean)
          .map((f) => String(f).split("T")[0]); // "YYYY-MM-DD" se compara bien como texto
        if (fechas.length === 0) return "";
        return fechas.reduce((a, b) => (a > b ? a : b));
      };
      serviciosParseados.sort((a, b) => {
        const fa = fechaProximaMayor(a);
        const fb = fechaProximaMayor(b);
        if (fa === fb) return 0;
        if (!fa) return 1; // sin fechas, al final
        if (!fb) return -1;
        return fa < fb ? 1 : -1; // descendente
      });

      setServicios(serviciosParseados);

    } catch (err) {
      toast.error("❌ Error al cargar servicios");
    } finally {
      setLoading(false);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "—";
    const limpia = fecha.split("T")[0];
    const [anio, mes, dia] = limpia.split("-");
    return `${dia}/${mes}/${anio}`;
  };

  // 🔎 Busca el cambio de pezoneras INMEDIATAMENTE siguiente del mismo cliente.
  // Si existe, el conteo de ordeñes de este registro se congela en esa fecha.
  const buscarCambioSiguiente = (s) => {
    const parsear = (f) => (f ? new Date(String(f).replace(/-/g, '/')) : null);
    const inicio = parsear(s?.fecha_cambio_pezonera);
    if (!inicio || isNaN(inicio)) return null;

    let siguiente = null;
    for (const sig of servicios) {
      if (sig.id === s.id || sig.cliente_id !== s.cliente_id) continue;
      const fecha = parsear(sig?.fecha_cambio_pezonera);
      if (!fecha || isNaN(fecha) || fecha <= inicio) continue;
      if (!siguiente || fecha < siguiente) siguiente = fecha;
    }
    return siguiente;
  };

  const abrirImagen = (imagenes) => {
    setImagenesModal(imagenes);
    setIndiceImagen(0);
    setZoom(1);
    setRotacion(0);
  };

  const irDerecha = () => setIndiceImagen((i) => (i < imagenesModal.length - 1 ? i + 1 : 0));
  const irIzquierda = () => setIndiceImagen((i) => (i > 0 ? i - 1 : imagenesModal.length - 1));

  // Foto actual del visor, en versión optimizada
  const imagenActual = imagenesModal.length > 0 ? urlOptimizada(imagenesModal[indiceImagen]) : "";
  const cargandoImagen = imagenActual !== "" && imagenActual !== imagenCargada;

  // Precarga la foto siguiente y la anterior para que aparezcan al instante
  useEffect(() => {
    if (imagenesModal.length < 2) return;
    precargarImagen(imagenesModal[(indiceImagen + 1) % imagenesModal.length]);
    precargarImagen(imagenesModal[(indiceImagen - 1 + imagenesModal.length) % imagenesModal.length]);
  }, [imagenesModal, indiceImagen]);


  // Opciones del buscador de clientes
  const opcionesClientes = [
    { value: "todos", label: "TODOS LOS CLIENTES" },
    ...clientes.map(c => ({ value: c.id, label: c.nombre }))
  ];
  const opcionSeleccionada = opcionesClientes.find(o => String(o.value) === String(clienteSeleccionado)) || null;

  return (
    <div className="h-full flex flex-col font-sans text-slate-900 relative">
      <Toaster position="top-center" />

      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        {/* 🔍 PANEL DE FILTROS: filtra apenas se elige un cliente */}
        <div className="shrink-0 px-3 py-2.5 border-b border-slate-200 bg-white flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Select
              options={opcionesClientes}
              value={opcionSeleccionada}
              onChange={(opcion) => {
                const valor = opcion ? opcion.value : "";
                setClienteSeleccionado(valor);
                obtenerServicios(valor);
              }}
              isClearable
              placeholder="Buscar cliente..."
              noOptionsMessage={() => "Sin coincidencias"}
              className="w-full md:w-80 text-[13px]"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: '0.5rem',
                  borderColor: state.isFocused ? '#3b82f6' : '#e2e8f0',
                  minHeight: '38px',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  '&:hover': { borderColor: '#3b82f6' }
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '13px',
                  backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
                  color: state.isSelected ? 'white' : '#0f172a',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              menuPortalTarget={document.body}
            />
            <button
              onClick={() => obtenerServicios(clienteSeleccionado)}
              title="Actualizar"
              className="p-2.5 bg-slate-900 text-white rounded-lg hover:bg-blue-600 transition-all shadow-sm active:scale-95 shrink-0"
            >
              <RotateCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-semibold text-slate-500">
            {/* Selector de densidad (solo afecta la tabla de escritorio) */}
            <div className="hidden lg:flex items-center gap-2">
              <span className="text-slate-400">Vista:</span>
              <div className="flex p-0.5 bg-slate-100 rounded-lg border border-slate-200">
                {[
                  { valor: "comoda", label: "Cómoda" },
                  { valor: "compacta", label: "Compacta" },
                ].map((op) => (
                  <button
                    key={op.valor}
                    onClick={() => setDensidad(op.valor)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${densidad === op.valor
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                      }`}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            </div>
            <span className="hidden lg:inline-block h-4 w-px bg-slate-200" />
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Dentro de vida útil</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Superó vida útil</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400" /> Fecha manual</span>
            <span className="hidden md:inline-block h-4 w-px bg-slate-200" />
            <span className="text-slate-900 font-black">{servicios.length} <span className="text-slate-400 font-semibold">registros</span></span>
          </div>
        </div>

        {/* 📱 VISTA MOBILE: CARDS */}
        <div className="lg:hidden flex-1 min-h-0 overflow-y-auto scroll-fino bg-slate-100">
          {loading ? (
            <div className="py-20 text-center text-slate-400 text-xs italic font-medium">Cargando base de datos...</div>
          ) : servicios.length === 0 ? (
            <div className="py-20 text-center text-slate-400 text-xs italic font-medium">No hay servicios para mostrar</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3">
              {servicios.map((s) => {
                // 1. Convertimos la fecha de inicio del servicio actual
                const fechaInicio = s?.fecha_cambio_pezonera ? new Date(s?.fecha_cambio_pezonera?.replace(/-/g, '/')) : null;

                // 2. Buscamos si este mismo cliente tiene un cambio de pezoneras posterior para "cerrar" el conteo
                const cambioSiguiente = buscarCambioSiguiente(s);

                let fechaFin;
                if (cambioSiguiente) {
                  // CASO HISTORIAL: Se congela en la fecha del siguiente cambio
                  fechaFin = cambioSiguiente;
                } else {
                  // CASO USO ACTUAL: Sigue sumando día a día hasta hoy
                  fechaFin = new Date();
                  fechaFin.setHours(0, 0, 0, 0);
                }

                // 3. Aplicamos la fórmula validada con tu imagen
                let ordeñesPorPezonera = 0;
                if (fechaInicio && !isNaN(fechaInicio) && !isNaN(fechaFin)) {
                  const diffTiempo = fechaFin - fechaInicio;
                  const diasTranscurridos = Math.floor(diffTiempo / (1000 * 60 * 60 * 24));
                  const diasEfectivos = diasTranscurridos > 0 ? diasTranscurridos : 0;

                  const bajadas = Number(s?.num_de_bajadas) || 1;
                  const vacas = Number(s?.vacas_en_ordenio) || 0;
                  const ordeniosDia = Number(s?.ordenios_por_dia) || 0;

                  // (Días / Bajadas) * Vacas * Ordeños_Día
                  ordeñesPorPezonera = (diasEfectivos / bajadas) * vacas * ordeniosDia;
                }

                // AGREGÁ ESTA LÍNEA AQUÍ:
                const superaLimite = ordeñesPorPezonera > (Number(s?.vida_util_pezonera) || 0);

                return (
                  <div key={s.id} className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden relative ">
                    <div className="bg-slate-900 p-3 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${superaLimite ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                        <h3 className="font-black text-white text-[13px] uppercase truncate max-w-[200px]">{s.cliente?.nombre || "—"}</h3>
                      </div>
                      <div className="flex gap-2">
                        <BotonEditar idServicio={s.id} />
                        <BotonEliminar
                          idServicio={s.id}
                          nombreCliente={s.cliente?.nombre}
                          onDeleteSuccess={() => obtenerServicios(clienteSeleccionado)}
                        />
                      </div>
                    </div>

                    <div className="p-4 grid grid-cols-2 gap-3">
                      <div className="col-span-1">
                        <p className="text-[9px] uppercase text-slate-400 font-bold">Último Servicio técnico</p>
                        <p className="text-xs font-bold text-slate-700">{formatearFecha(s?.fecha_servicio)}</p>
                      </div>
                      <div className="col-span-1 text-right">
                        <p className="text-[9px] uppercase text-slate-400 font-bold">Próximo Servicio técnico</p>
                        <p className={`text-xs font-bold ${s?.proximo_servicio_manual ? "text-orange-600" : "text-slate-700"}`}>{formatearFecha(s?.proximo_servicio)}</p>
                      </div>
                      <div className="col-span-1 ">
                        <p className="text-[9px] uppercase text-slate-400 font-bold">Último Cambio de Pezoneras</p>
                        <p className={"text-xs font-bold text-slate-700"}>{formatearFecha(s?.fecha_cambio_pezonera)}</p>
                      </div>
                      <div className="col-span-1 text-right">
                        <p className="text-[9px] uppercase text-slate-400 font-bold">Próximo Cambio de Pezoneras</p>
                        <p className={`text-xs font-bold ${s?.proximo_recambio_manual ? "text-orange-600" : "text-blue-900"}`}>{formatearFecha(s?.proximo_recambio_pezoneras)}</p>

                      </div>
                      {/* <div className="col-span-2 bg-blue-50 p-2 rounded-lg border border-blue-100 flex justify-between items-center"> */}
                      {/* <div>
                          <p className="text-[9px] uppercase text-blue-400 font-black tracking-tight">Próx. Recambio Pezoneras</p>
                          <p className={`text-sm font-black ${s.proximo_recambio_manual ? "text-orange-600" : "text-blue-900"}`}>{formatearFecha(s.proximo_recambio_pezoneras)}</p>
                        </div> */}
                      <div className="text-center p-2">
                        <p className="text-[8px] uppercase text-blue-400 font-bold tracking-tight">Cantidad Ordeñes por Pezonera</p>
                        <span className={`inline-block px-2 py-0 rounded text-center text-xs font-black ${superaLimite ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
                          {Math.round(ordeñesPorPezonera).toLocaleString()}
                        </span>
                      </div>
                      {/* </div> */}
                      <div className="bg-slate-50 p-2 rounded-lg text-center border border-slate-100">
                        <p className="text-[8px] uppercase text-slate-400 font-bold">Ordeños por día</p>
                        <p className="text-xs font-black text-slate-800">{s?.ordenios_por_dia || 0}</p>
                      </div>
                      <div className="bg-slate-50 p-2 rounded-lg text-center border border-slate-100">
                        <p className="text-[8px] uppercase text-slate-400 font-bold">Servicio programado cada</p>
                        <p className="text-xs font-black text-slate-800">{s?.servicio_prog_cada || 0} meses</p>
                      </div>
                      <div className="bg-slate-50 p-2 rounded-lg text-center border border-slate-100">
                        <p className="text-[8px] uppercase text-slate-400 font-bold">Cantidad de puestos de ordeñe</p>
                        <p className="text-xs font-black text-slate-800">{s?.num_de_bajadas || 0}</p>
                      </div>
                      <div className="bg-slate-50 p-2 rounded-lg text-center border border-slate-100">
                        <p className="text-[8px] uppercase text-slate-400 font-bold">Vida Útil de pezonera</p>
                        <p className="text-xs font-black text-slate-800">{s?.vida_util_pezonera || 0}</p>
                      </div>
                      <div className="bg-slate-50 p-2 rounded-lg text-center border border-slate-100">
                        <p className="text-[8px] uppercase text-slate-400 font-bold">Vacas en ordeño</p>
                        <p className="text-xs font-black text-slate-800">{s?.vacas_en_ordenio || 0}</p>
                      </div>
                      <div className="bg-slate-50 p-2 rounded-lg text-center border border-slate-100">
                        <p className="text-[8px] uppercase text-slate-400 font-bold">Días Útiles de pezonera</p>
                        <p className="text-xs font-black text-slate-800">{s?.dias_utiles_pezonera || 0}</p>
                      </div>
                      <div className="col-span-2 flex gap-2 pt-1">
                        <button onClick={() => s.cliente?.descripcion_ordeniadora && setModeloOrdeniadora({ texto: s.cliente.descripcion_ordeniadora, cliente: s.cliente.nombre })} disabled={!s.cliente?.descripcion_ordeniadora} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-[10px] font-bold uppercase transition-all ${s.cliente?.descripcion_ordeniadora ? 'bg-white border-slate-200 text-slate-700 active:scale-95' : 'bg-slate-50 border-transparent text-slate-300'}`}><Eye size={14} /> Modelo</button>
                        {/* <button onClick={() => s.imagenes_remito?.length > 0 && abrirImagen(s.imagenes_remito)} disabled={!s.imagenes_remito?.length} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-[10px] font-bold uppercase transition-all ${s.imagenes_remito?.length > 0 ? 'bg-blue-50 border-blue-100 text-blue-600 active:scale-95' : 'bg-slate-50 border-transparent text-slate-300'}`}><ImageIcon size={14} /> Remito</button> */}
                        {/* nuevo */}
                        <button
                          onClick={() => s.imagenes_remito?.length > 0 && abrirImagen(s.imagenes_remito)}
                          disabled={!s.imagenes_remito?.length}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-[10px] font-bold uppercase transition-all relative ${s.imagenes_remito?.length > 0
                            ? 'bg-blue-50 border-blue-100 text-blue-600 active:scale-95'
                            : 'bg-slate-50 border-transparent text-slate-300'
                            }`}
                        >
                          <div className="relative">
                            <ImageIcon size={14} />
                            {/* Globito rojo con la cantidad */}
                            {s.imagenes_remito?.length > 0 && (
                              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center shadow-sm border border-white">
                                {s.imagenes_remito.length}
                              </span>
                            )}
                          </div>
                          Remito
                        </button>

                        <button onClick={() => s.comentario && setComentarioModal({ texto: s.comentario, cliente: s.cliente?.nombre })} disabled={!s.comentario} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-[10px] font-bold uppercase transition-all ${s.comentario ? 'bg-white border-slate-200 text-slate-700 active:scale-95' : 'bg-slate-50 border-transparent text-slate-300'}`}><Eye size={14} /> Nota</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 💻 VISTA DESKTOP: TABLA (ocupa todo el alto disponible, encabezado fijo) */}
        <div className="hidden lg:block flex-1 min-h-0 overflow-auto scroll-fino relative">
            <table className="w-full border-separate border-spacing-0 text-left min-w-[1000px] tabular-nums">
              <thead className="sticky top-0 z-30 bg-slate-900 text-white">
                <tr className={`[&>th]:px-2 ${compacta ? "[&>th]:py-2" : "[&>th]:py-2.5"} [&>th]:text-[10px] [&>th]:font-bold [&>th]:uppercase [&>th]:tracking-wide [&>th]:whitespace-nowrap [&>th]:text-center [&>th]:align-middle [&>th]:border-r [&>th]:border-slate-700/60 [&>th]:cursor-default`}>
                  <th className="sticky left-0 z-40 bg-slate-900 !text-left !px-3 min-w-[170px] w-[16%]">
                    Cliente
                  </th>
                  <th title="Último servicio técnico">Últ. serv.</th>
                  <th title="Próximo servicio técnico">Próx. serv.</th>
                  <th title="Último cambio de pezoneras">Últ. pezon.</th>
                  <th title="Próximo cambio de pezoneras">Próx. pezon.</th>
                  <th title="Ordeños por día">Ord./día</th>
                  <th title="Servicio programado cada">Serv. cada</th>
                  <th title="Cantidad de puestos de ordeñe">Puestos</th>
                  <th title="Vida útil de pezonera">Vida útil</th>
                  <th title="Vacas en ordeño">Vacas</th>
                  <th title="Días útiles de pezonera">Días útiles</th>
                  <th title="Cantidad de ordeñes por pezonera" className="bg-slate-800">Ordeñes/pezon.</th>
                  <th title="Modelo · Remito · Nota">Detalle</th>
                  <th className="sticky right-0 z-40 bg-slate-900 !border-r-0 border-l border-slate-700 w-[80px]">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">

                {!loading && servicios.map((s, index) => {
                  // 1. Fecha en que se colocaron las pezoneras en ESTE servicio
                  const fechaInicio = s?.fecha_cambio_pezonera ? new Date(s?.fecha_cambio_pezonera?.replace(/-/g, '/')) : null;

                  // 2. Buscamos el servicio SIGUIENTE del MISMO cliente para cerrar el ciclo
                  // Si no existe uno más reciente, es porque este es el "Uso Actual"
                  const cambioSiguiente = buscarCambioSiguiente(s);

                  let fechaFin;
                  if (cambioSiguiente) {
                    // Es un servicio viejo: El conteo se detiene en el siguiente cambio de pezoneras
                    fechaFin = cambioSiguiente;
                  } else {
                    // Es el servicio más reciente: El conteo sigue hasta hoy
                    fechaFin = new Date();
                    fechaFin.setHours(0, 0, 0, 0);
                  }

                  let ordeñesPorPezonera = 0;

                  if (fechaInicio && !isNaN(fechaInicio) && !isNaN(fechaFin)) {
                    const dias = Math.floor((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24));
                    const diasEfectivos = dias > 0 ? dias : 0;

                    // Fórmula exacta de tu imagen: (Días / Bajadas) * Vacas * Ordeñes/Día
                    ordeñesPorPezonera = (diasEfectivos / (Number(s?.num_de_bajadas) || 1)) * (Number(s?.vacas_en_ordenio) || 0) * (Number(s?.ordenios_por_dia) || 0);
                  }

                  const superaLimite = ordeñesPorPezonera > (Number(s?.vida_util_pezonera) || 0);

                  // Estilos según la densidad elegida (solo visual)
                  const alto = compacta ? "py-1" : "py-2";
                  const celda = `px-2 ${alto} text-[12px] leading-5 text-center text-slate-800 border-b border-r border-slate-100 whitespace-nowrap`;
                  const zebra = index % 2 === 0 ? "bg-white" : "bg-slate-50/70";
                  const boton = compacta ? "p-1" : "p-1.5";
                  const icono = compacta ? 14 : 15;

                  return (
                    <tr key={s.id} className={`group ${zebra} hover:bg-blue-100 transition-colors`}>
                      <td className={`sticky left-0 z-20 ${index % 2 === 0 ? "bg-white" : "bg-slate-50"} group-hover:bg-blue-100 border-r border-b border-slate-200 px-3 ${alto} text-[12px] leading-5 font-bold text-slate-900`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 shrink-0 rounded-full ${superaLimite ? 'bg-red-500' : 'bg-emerald-500'}`} />
                          <span className="truncate uppercase" title={s.cliente?.nombre}>{s.cliente?.nombre || "—"}</span>
                        </div>
                      </td>
                      <td className={celda}>{formatearFecha(s.fecha_servicio)}</td>
                      <td className={`${celda} font-bold ${s.proximo_servicio_manual ? "!text-orange-600 bg-orange-50" : "!text-slate-900"}`}>{formatearFecha(s?.proximo_servicio)}</td>
                      <td className={celda}>{formatearFecha(s?.fecha_cambio_pezonera)}</td>
                      <td className={`${celda} font-bold ${s.proximo_recambio_manual ? "!text-orange-600 bg-orange-50" : "!text-slate-900"}`}>{formatearFecha(s?.proximo_recambio_pezoneras)}</td>
                      <td className={celda}>{s.ordenios_por_dia}</td>
                      <td className={celda}>{s.servicio_prog_cada ? s.servicio_prog_cada + " meses" : ""}</td>
                      <td className={celda}>{s.num_de_bajadas}</td>
                      <td className={celda}>{s.vida_util_pezonera}</td>
                      <td className={celda}>{s.vacas_en_ordenio}</td>
                      <td className={celda}>{s.dias_utiles_pezonera}</td>
                      <td className={celda}><span className={`inline-block min-w-[56px] px-2 ${compacta ? "py-0 leading-5" : "py-0.5"} rounded-md text-[11px] font-black ${superaLimite ? 'bg-red-500 text-white' : 'bg-emerald-100 text-emerald-800'}`}>{Math.round(ordeñesPorPezonera).toLocaleString()}</span></td>
                      <td className={celda}>
                        <div className="flex items-center justify-center gap-1">
                          {/* Modelo */}
                          <button
                            onClick={() => s.cliente?.descripcion_ordeniadora && setModeloOrdeniadora({ texto: s.cliente.descripcion_ordeniadora, cliente: s.cliente.nombre })}
                            disabled={!s.cliente?.descripcion_ordeniadora}
                            title="Modelo de ordeñadora"
                            className={`${boton} rounded-md text-blue-500 hover:bg-blue-100 hover:text-blue-700 transition-colors disabled:text-slate-200 disabled:hover:bg-transparent`}
                          >
                            <Eye size={icono} />
                          </button>
                          {/* Remito */}
                          <button
                            onClick={() => s.imagenes_remito?.length > 0 && abrirImagen(s.imagenes_remito)}
                            disabled={!(s.imagenes_remito?.length > 0)}
                            title="Remito"
                            className={`relative ${boton} rounded-md text-blue-600 hover:bg-blue-600 hover:text-white transition-all disabled:text-slate-200 disabled:hover:bg-transparent`}
                          >
                            <ImageIcon size={icono} />
                            {s.imagenes_remito?.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-bold border border-white">{s.imagenes_remito.length}</span>
                            )}
                          </button>
                          {/* Nota */}
                          <button
                            onClick={() => s.comentario && setComentarioModal({ texto: s.comentario, cliente: s.cliente?.nombre })}
                            disabled={!s.comentario}
                            title="Nota"
                            className={`${boton} rounded-md text-slate-500 hover:bg-blue-100 hover:text-blue-600 transition-colors disabled:text-slate-200 disabled:hover:bg-transparent`}
                          >
                            <StickyNote size={icono} />
                          </button>
                        </div>
                      </td>
                      <td className={`sticky right-0 z-20 ${index % 2 === 0 ? "bg-white" : "bg-slate-50"} group-hover:bg-blue-100 border-l border-b border-slate-200 px-2 ${compacta ? "py-0.5" : "py-1.5"} text-center`}>
                        <div className={`flex items-center justify-center gap-1 ${compacta ? "[&_button]:p-1 [&_svg]:w-4 [&_svg]:h-4" : ""}`}>
                          <BotonEditar idServicio={s.id} />
                          <BotonEliminar idServicio={s.id} nombreCliente={s.cliente?.nombre} onDeleteSuccess={() => obtenerServicios(clienteSeleccionado)} />
                        </div>
                      </td>
                    </tr>
                  );
                })
                }
              </tbody>
            </table>
            {loading && (
              <div className="py-20 text-center text-slate-400 text-xs italic font-medium">Cargando base de datos...</div>
            )}
            {!loading && servicios.length === 0 && (
              <div className="py-20 text-center text-slate-400 text-xs italic font-medium">No hay servicios para mostrar</div>
            )}
        </div>
      </div>


      {/* --- MODALES --- */}
      {imagenesModal.length > 0 && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl flex flex-col items-center">
            <div className="absolute -top-14 flex gap-2 bg-white p-2 rounded-xl shadow-2xl z-[210] border border-slate-100">
              <button onClick={() => setRotacion(r => r + 90)} className="p-2 hover:bg-slate-100 rounded-lg text-blue-600"><RotateCw size={18} /></button>
              <button onClick={irIzquierda} className="p-2 hover:bg-slate-100 rounded-lg font-bold text-xs">Anterior</button>
              <button onClick={() => setZoom(z => Math.min(z + 0.2, 3))} className="p-2 hover:bg-slate-100 rounded-lg"><ZoomIn size={18} /></button>
              <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))} className="p-2 hover:bg-slate-100 rounded-lg"><ZoomOut size={18} /></button>
              <button onClick={irDerecha} className="p-2 hover:bg-slate-100 rounded-lg font-bold text-xs">Siguiente</button>
              <div className="w-[1px] bg-slate-200 mx-1"></div>
              <button onClick={() => setImagenesModal([])} className="p-2 hover:bg-red-50 rounded-lg text-red-600"><X size={18} /></button>
            </div>
            {/* Cargando... mientras llega la foto */}
            {cargandoImagen && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white pointer-events-none">
                <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="text-xs font-bold uppercase tracking-widest">Cargando imagen...</span>
              </div>
            )}
            <img
              key={imagenActual}
              src={imagenActual}
              alt="Remito"
              onLoad={() => setImagenCargada(imagenActual)}
              onError={() => setImagenCargada(imagenActual)}
              className={`max-h-[80vh] min-h-[200px] rounded shadow-2xl transition-[transform,opacity] duration-200 object-contain ${cargandoImagen ? "opacity-0" : "opacity-100"}`}
              style={{ transform: `scale(${zoom}) rotate(${rotacion}deg)` }}
            />
            {imagenesModal.length > 1 && (
              <span className="mt-3 px-3 py-1 rounded-full bg-white/90 text-slate-900 text-[11px] font-black">
                {indiceImagen + 1} / {imagenesModal.length}
              </span>
            )}
          </div>
        </div>
      )}

      {(comentarioModal || modeloOrdeniadora) && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-200">
            <div className="bg-slate-50 px-5 py-3 border-b flex justify-between items-center">
              <h2 className="font-black text-blue-900 uppercase text-[10px] tracking-widest">Detalles del Registro</h2>
              <button onClick={() => { setComentarioModal(null); setModeloOrdeniadora(null); }} className="text-slate-400 hover:text-red-500 transition-colors"><X size={18} /></button>
            </div>
            <div className="p-5">
              <p className="text-[9px] uppercase font-bold text-slate-400 mb-1">Cliente Vinculado</p>
              <p className="text-sm font-black text-slate-900 mb-4 uppercase">{(comentarioModal || modeloOrdeniadora).cliente}</p>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-[13px] text-slate-900 leading-relaxed italic whitespace-pre-wrap">
                {(comentarioModal || modeloOrdeniadora).texto}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

